import React from 'react';
import DecoratedHeader from './DecoratedHeader';
import PhotoExampleComparison from './PhotoExampleComparison';
import PositionGuideSection from './PositionGuideSection';
import TipsCard from './TipsCard';
import { PHOTO_GUIDELINES_COPY } from './photoGuidelines.constants';
import type { PhotoGuidelinesProps } from './photoGuidelines.types';
import styles from './PhotoGuidelines.module.css';

import frontImage from '../../assets/images/photo_guidelines/front.jpeg';
import backImage from '../../assets/images/photo_guidelines/back.jpeg';
import rightSideImage from '../../assets/images/photo_guidelines/right_side.jpeg';
import leftSideImage from '../../assets/images/photo_guidelines/left_side.jpeg';
import positionStyles from './PositionGuideSection.module.css';

const PhotoGuidelines: React.FC<PhotoGuidelinesProps> = ({
  photos,
  missingSlots = [],
  onSelectPhoto,
  onRemovePhoto,
}) => {
  const copy = PHOTO_GUIDELINES_COPY;
  const missingSlotSet = new Set(missingSlots);

  return (
    <div className={styles.container}>
      <DecoratedHeader title={copy.headerTitle} />

      <p className={styles.introText}>
        {copy.intro.beforeBold}
        <strong className={styles.boldText}>{copy.intro.bold}</strong>
        {copy.intro.afterBold.split('\n\n').map((paragraph, index) => (
          <React.Fragment key={paragraph}>
            {index === 0 ? ' ' : (
              <>
                <br />
                <br />
              </>
            )}
            {paragraph}
          </React.Fragment>
        ))}
      </p>

      <h2 className={styles.sectionTitle}>{copy.orientationsTitle}</h2>

      <div className={styles.guidelinesList}>
        {copy.guidelines.map((guideline, index) => (
          <p key={guideline} className={styles.guidelineText}>
            <strong className={styles.numberText}>{index + 1}.</strong> {guideline}
            {index === 3 && <PhotoExampleComparison />}
          </p>
        ))}
      </div>

      <h3 className={styles.positionTitle}>{copy.tipsTitle}</h3>

      <TipsCard title={copy.avoidTitle} items={copy.avoidItems} variant="avoid" />
      <TipsCard
        title={copy.howToTitle}
        subtitle={copy.howToSubtitle}
        items={copy.howToItems}
        variant="howTo"
      />

      <PositionGuideSection
        title={copy.positions.front.title}
        imageSrc={frontImage}
        imageAlt="Pose frontal para foto postural"
        positionKey="front"
        imageClassName={positionStyles.frontImageAdjusted}
        instructions={copy.positions.front.instructions}
        photo={photos.front}
        slot="front"
        showMissingHint={missingSlotSet.has('front')}
        onSelectPhoto={onSelectPhoto}
        onRemovePhoto={onRemovePhoto}
      />

      <PositionGuideSection
        title={copy.positions.back.title}
        imageSrc={backImage}
        imageAlt="Pose de costas para foto postural"
        positionKey="back"
        imageClassName={positionStyles.zoomedImage1_1}
        instructions={copy.positions.back.instructions}
        photo={photos.back}
        slot="back"
        showMissingHint={missingSlotSet.has('back')}
        onSelectPhoto={onSelectPhoto}
        onRemovePhoto={onRemovePhoto}
      />

      <PositionGuideSection
        title={copy.positions.right_side.title}
        imageSrc={rightSideImage}
        imageAlt="Pose do lado direito para foto postural"
        positionKey="right_side"
        instructions={copy.positions.right_side.instructions}
        photo={photos.rightSide}
        slot="rightSide"
        showMissingHint={missingSlotSet.has('rightSide')}
        onSelectPhoto={onSelectPhoto}
        onRemovePhoto={onRemovePhoto}
      />

      <PositionGuideSection
        title={copy.positions.left_side.title}
        imageSrc={leftSideImage}
        imageAlt="Pose do lado esquerdo para foto postural"
        positionKey="left_side"
        imageClassName={positionStyles.zoomedImage1_1}
        instructions={copy.positions.left_side.instructions}
        photo={photos.leftSide}
        slot="leftSide"
        showMissingHint={missingSlotSet.has('leftSide')}
        onSelectPhoto={onSelectPhoto}
        onRemovePhoto={onRemovePhoto}
      />
    </div>
  );
};

export default PhotoGuidelines;
